import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { getCurrentUser } from '../lib/auth';
// ═══════════════════════════════════════════════════════════════════════════
// AUTO SHELVES
// Called whenever a book is added to the library.
// Reads the book's genres + tropes, creates a shelf for each one
// if it doesn't exist, then links the library entry to those shelves.
// ═══════════════════════════════════════════════════════════════════════════

// Call this after any addToShelf / handleAddToLibrary
export async function autoShelfBook(userLibraryId, bookData, userId) {
  if (!userLibraryId || !userId) return;

  try {
    const genres = [];

    // Collect genres from the book
    if (Array.isArray(bookData.genres)) {
      genres.push(...bookData.genres.map(g => ({ label: g, source: 'genre' })));
    }

    // Collect tropes (from book_tags for platform books, trope names for CSV)
    if (Array.isArray(bookData.tropes)) {
      genres.push(...bookData.tropes.map(t => ({ label: t, source: 'trope' })));
    }

    if (genres.length === 0) return;

    // Deduplicate
    const unique = [...new Map(genres.map(g => [`${g.source}:${g.label}`, g])).values()];

    // Upsert each genre shelf (creates if not exists, skips if exists)
    const shelfUpserts = unique.map(g => ({
      user_id: userId,
      genre: g.label,
      source: g.source,
    }));

    const { error: shelfError } = await supabase
      .from('user_auto_shelves')
      .upsert(shelfUpserts, { onConflict: 'user_id,genre,source', ignoreDuplicates: true });

    if (shelfError) {
      console.error('❌ user_auto_shelves insert failed:', shelfError.message);
      return;
    }

    const entryUpserts = unique.map(g => ({
      user_library_id: userLibraryId,
      user_id: userId,
      genre: g.label,
      source: g.source,
    }));

    const { error: entryError } = await supabase
      .from('user_library_auto_shelf_entries')
      .upsert(entryUpserts, { onConflict: 'user_library_id,genre,source', ignoreDuplicates: true });

    if (entryError) {
      console.error('❌ user_library_auto_shelf_entries insert failed:', entryError.message);
      return;
    }

    console.log(`✅ Auto-shelved into: ${unique.map(g => g.label).join(', ')}`);
  } catch (err) {
    console.error('Auto-shelf error:', err);
  }
}

// ── Hook: fetch all auto shelves + their book counts for a user ────────────
export function useAutoShelves() {
  const [shelves, setShelves] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchAutoShelves(); }, []);

  async function fetchAutoShelves() {
    setLoading(true);
    try {
      const user = await getCurrentUser();
      if (!user) { setLoading(false); return; }

      // Get all auto shelves + count of books in each
      const { data, error } = await supabase
        .from('user_library_auto_shelf_entries')
        .select('genre, source, user_library_id')
        .eq('user_id', user.id);

      if (error) throw error;

      // Count DISTINCT books per genre — deduplicate by user_library_id
      // to avoid showing wrong count when same book was added multiple times
      const seen = {};   // key → Set of user_library_ids
      const sources = {};
      (data || []).forEach(row => {
        const key = `${row.source}:${row.genre}`;
        if (!seen[key]) seen[key] = new Set();
        seen[key].add(row.user_library_id);
        sources[key] = row.source;
      });

      const counts = {};
      Object.entries(seen).forEach(([key, ids]) => {
        counts[key] = ids.size;
      });

      const formatted = Object.entries(counts)
        .map(([key, count]) => ({
          label: key.split(':').slice(1).join(':'),
          source: sources[key],
          count,
        }))
        .sort((a, b) => b.count - a.count);

      setShelves(formatted);
    } catch (err) {
      console.error('Error fetching auto shelves:', err);
      setShelves([]);
    } finally {
      setLoading(false);
    }
  }

  // Fetch books for a specific auto shelf
  async function getBooksForShelf(genre, source = 'genre') {
    const user = await getCurrentUser();
    if (!user) return [];

    const { data: entries } = await supabase
      .from('user_library_auto_shelf_entries')
      .select('user_library_id')
      .eq('user_id', user.id)
      .eq('genre', genre)
      .eq('source', source);

    if (!entries?.length) return [];

    const libraryIds = entries.map(e => e.user_library_id);

    const { data: libraryItems } = await supabase
      .from('user_library')
      .select(`
        id, shelf, rating, date_added,
        books (id, title, author_name, cover_image_url, avg_rating),
        csv_book_imports (id, title, author_name, cover_image_url, avg_rating)
      `)
      .in('id', libraryIds);

    return (libraryItems || []).map(item => {
      const book = item.books || item.csv_book_imports;
      return {
        id: book?.id,
        title: book?.title,
        author: book?.author_name,
        cover: book?.cover_image_url,
        rating: item.rating,
        shelf: item.shelf,
      };
    });
  }

  return { shelves, loading, getBooksForShelf, refetch: fetchAutoShelves };
}